import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { OfficePage } from "../../components/OfficePage";
import { OfficeBox } from "../../components/OfficeBox";
import { styles } from "../PartnersScreen/partners.styles";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { CustomPagination } from "../../components/CustomPagination";
import { PartnersNoSearchResultsOverlay } from "./PartnersNoSearchResultsOverlay";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { PartnersHeadSection } from "../PartnersScreen/PartnersHeadSection";
import { PartnersSearchSortSection } from "../PartnersScreen/PartnersSearchSortSection";
import { PartnersContext } from "../../hooks/PartnersContext";
import { columns } from "../PartnersScreen/PartnersDataGridComponents/format-partners-columns";


const PartnersSearchResultsScreen = ({ isWebView }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const searchQuery = params.get("searchTerm");
  const id = params.get("partnerId");
  const { rows,
    rowCount,
    loading,
    pageSize,
    page,
    setPage,
    setPageSize,
    columnVisibilityModel,
    setColumnVisibilityModel,
    sortModel,
    setSortModel,
    setDownlineUid,
    mobileColumn,
    setSearchQuery } = useContext(PartnersContext);

  const apiRef = useGridApiRef();
  const theme = useTheme();
  const viewportIsSmall = useMediaQuery(theme.breakpoints.up("sm"));
  const viewportIsMedium = useMediaQuery(theme.breakpoints.up("md"));
  const viewportIsLarge = useMediaQuery(theme.breakpoints.up("lg"));

  // set downlineUid to current user if viewing search screen
  useEffect(() => {
    if (id) {
      setDownlineUid(id);
    }
    // eslint-disable-next-line
  }, [id])

  useEffect(() => {
    if (searchQuery) {
      setSearchQuery(searchQuery);
    }
    // default to relevance sort on load for search results
    setSortModel([{ field: "relevance", sort: "desc" }]);
    // eslint-disable-next-line
  }, [])

  // for small screens, show just one column, unless sorted by name, then display totalOv
  useEffect(() => {
    // Show only Name and one other column IF
    // 1. viewport is not at least medium
    // 2. logged in user is not in context (genealogy and stats render) AND viewport is not large
    // ELSE show all columns
    if (!viewportIsMedium) {
      setColumnVisibilityModel({
        nextRank: mobileColumn === "nextRank",
        centurionCount: mobileColumn === "centurionCount",
        newPartners: mobileColumn === "newPartners",
        cpv: mobileColumn === "cpv",
        uncompressedTv: mobileColumn === "uncompressedTv",
        totalOv:
          mobileColumn === "totalOv",
        relevance: false
      });
    } else {
      setColumnVisibilityModel({
        nextRank: true,
        newPartners: true,
        centurionCount: true,
        cpv: true,
        uncompressedTv: true,
        totalOv: true,
        relevance: false
      });
    }
  }, [
    viewportIsSmall,
    setColumnVisibilityModel,
    viewportIsLarge,
    viewportIsMedium,
    apiRef,
    mobileColumn
  ]);

  // prevent default MUI sorting behavior when clicking mobile help icon
  const handleColumnHeaderClick = (params, event) => {
    if (event.target.getAttribute("data-test-id") === "help-icon-mobile") {
      event.defaultMuiPrevented = true;
    }
  }

  return (
    <OfficePage pageTitle="search_results" showBackArrow={true}>
      <PartnersSearchSortSection />
      <OfficeBox>
        <PartnersHeadSection isWebView={isWebView} />
        <Box
          sx={{
            height: `calc(100dvh - ${viewportIsSmall ? "200px" : isWebView ? "140px" : "272px"
              })`,
            minHeight: "315px",
          }}
        >
          <DataGridPro
            apiRef={apiRef}
            columns={columns}
            onColumnHeaderClick={handleColumnHeaderClick}
            rows={rows}
            pageSize={pageSize}
            columnHeaderHeight={36}
            columnVisibilityModel={columnVisibilityModel}
            sx={styles.dataGrid}
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            loading={loading}
            rowCount={rowCount}
            onPageChange={setPage}
            rowsPerPageOptions={[10, 50, 100]}
            onPageSizeChange={setPageSize}
            sortingOrder={["desc", "asc"]}
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            pagination
            paginationMode="server"
            sortingMode="server"
            disableVirtualization
            slots={{
              pagination: CustomPagination,
              noRowsOverlay: PartnersNoSearchResultsOverlay,
              noResultsOverlay: PartnersNoSearchResultsOverlay,
            }}
            slotProps={{
              pagination: {
                page: page,
                pageSize: pageSize,
                rowCount: rowCount,
                handlePageSizeChange: setPageSize,
                loading: loading,
                handlePageChange: setPage,
                isPartners: true,
              },
            }}
          />
        </Box>
      </OfficeBox>
    </OfficePage>
  )
}

export { PartnersSearchResultsScreen }